import React, { useEffect, useMemo, useState } from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import RestaurantDashboardLayout from './RestaurantDashboardLayout';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import { fetchJobProfileByCandidateId } from './jobProfileApi';
import {
  formatHiringLocationPrimary,
  formatHiringLocationSecondary,
  getApplicantInitials,
  getHiringLocationKey,
} from './hiringMockData';
import { formatMobileDisplayOrFallback } from './formatDisplay';

import './css/Hiring.css';
import './css/Restaurants.css';
import './css/OrderDetails.css';
import './css/Inventory.css';
import './css/UserManagement.css';

const HIRING_PATH = '/home?tab=hiring';

const displayValue = (value) => {
  const text = value != null ? String(value).trim() : '';
  return text || '—';
};

function formatDetailDate(value) {
  if (value == null || String(value).trim() === '') return '—';
  const s = String(value).trim().replace(' ', 'T');
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${mins}`;
}

function formatSalary(value) {
  if (value == null || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value);
  return `₹${amount.toLocaleString('en-IN')}`;
}

function formatYesNo(value) {
  if (value == null) return '—';
  return value ? 'Yes' : 'No';
}

function formatStatus(status) {
  if (!status) return '—';
  return status === 'ACTIVE' ? 'Open to work' : String(status);
}

const DetailField = ({ label, value, wide = false, children }) => (
  <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
    <span className="restaurant-detail-label">{label}</span>
    {children ?? <span className="restaurant-detail-value">{displayValue(value)}</span>}
  </div>
);

const HiringCandidateDetail = () => {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const { enabledTypes: enabledServiceTypes } = useRestaurantEnabledServices();
  const location = useLocation();
  const prefetchedCandidate = location.state?.candidate;
  const [candidate, setCandidate] = useState(() => {
    const rawId = candidateId != null ? String(candidateId).trim() : '';
    const idNum = Number(rawId);
    if (
      prefetchedCandidate &&
      Number.isFinite(idNum) &&
      Number(prefetchedCandidate.id) === idNum
    ) {
      return prefetchedCandidate;
    }
    return null;
  });
  const [loading, setLoading] = useState(() => {
    const rawId = candidateId != null ? String(candidateId).trim() : '';
    const idNum = Number(rawId);
    if (
      prefetchedCandidate &&
      Number.isFinite(idNum) &&
      Number(prefetchedCandidate.id) === idNum
    ) {
      return false;
    }
    return true;
  });
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const rawId = candidateId != null ? String(candidateId).trim() : '';
    const idNum = Number(rawId);

    if (!rawId || Number.isNaN(idNum)) {
      setLoading(false);
      setError('Invalid candidate id.');
      setCandidate(null);
      return undefined;
    }

    (async () => {
      const hasPrefetch =
        prefetchedCandidate &&
        Number.isFinite(idNum) &&
        Number(prefetchedCandidate.id) === idNum;

      if (!hasPrefetch) {
        setLoading(true);
        setCandidate(null);
      }
      setError('');
      try {
        const data = await fetchJobProfileByCandidateId(idNum);
        if (cancelled) return;
        setCandidate(data);
      } catch (e) {
        if (cancelled) return;
        if (!hasPrefetch) {
          setCandidate(null);
        }
        setError(
          e?.response?.data?.message ||
            e?.message ||
            'Failed to load candidate details.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [candidateId, prefetchedCandidate]);

  const profile = candidate?.raw ?? {};
  const preferredLocations = candidate?.preferredLocations ?? [];
  const title = candidate?.name ? String(candidate.name).trim() : 'Candidate';

  const statusLabel = useMemo(
    () => formatStatus(profile.status ?? candidate?.status),
    [profile.status, candidate?.status]
  );

  return (
    <RestaurantDashboardLayout activeTab="hiring" enabledServiceTypes={enabledServiceTypes}>
      <div className="hiring-candidate-detail-page">
        <div className="restaurant-detail-panel">
          <div className="restaurant-detail-card">
            <div className="restaurant-detail-card-header inventory-item-detail-card-header">
              <div className="inventory-item-detail-header-row hiring-candidate-detail-header-row">
                <button
                  type="button"
                  className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                  onClick={() => navigate(HIRING_PATH)}
                  aria-label="Back to hiring"
                >
                  <FaArrowLeft aria-hidden />
                </button>
                <div className="hiring-candidate-detail-title-wrap">
                  {candidate ? (
                    <div className="hiring-avatar hiring-avatar--detail" aria-hidden>
                      {getApplicantInitials(candidate.name)}
                    </div>
                  ) : null}
                  <h3 className="restaurant-detail-section-title">{title}</h3>
                </div>
              </div>
            </div>

            {loading ? (
              <p className="order-details-loading">Loading candidate details…</p>
            ) : null}

            {!loading && error ? (
              <p className="restaurant-detail-message error">{error}</p>
            ) : null}

            {!loading && !error && candidate ? (
              <>
                <div className="restaurant-detail-grid">
                  <DetailField label="Profile ID" value={profile.customerProfileId ?? profile.candidateId ?? candidate.id} />
                  <DetailField label="Customer ID" value={profile.customerId} />
                  <DetailField label="Industry" value={profile.industryName} />
                  <DetailField label="Role">
                    <span className="user-role-pill">{candidate.role}</span>
                  </DetailField>
                  <DetailField label="Status">
                    <span className="hiring-status-pill">{statusLabel}</span>
                  </DetailField>
                  <DetailField
                    label="Phone"
                    value={formatMobileDisplayOrFallback(candidate.phone, '—')}
                  />
                  <DetailField label="Email" value={candidate.email} />
                </div>
              </>
            ) : null}

            {!loading && !candidate && !error ? (
              <p className="order-details-empty">No detail data for this candidate.</p>
            ) : null}
          </div>

          {!loading && !error && candidate ? (
            <>
              <div className="restaurant-detail-card">
                <div className="restaurant-detail-card-header">
                  <h3 className="restaurant-detail-section-title">Experience</h3>
                </div>
                <div className="restaurant-detail-grid">
                  <DetailField
                    label="Experience (years)"
                    value={profile.experienceYears ?? candidate.experienceYears}
                  />
                  <DetailField label="Current company" value={profile.companyName} />
                  <DetailField label="Employment type" value={profile.employmentType} />
                  <DetailField label="Available to join" value={profile.availableToJoin} />
                  <DetailField label="Current city" value={profile.currentCity} />
                  <DetailField
                    label="Willing to relocate"
                    value={formatYesNo(profile.willingToRelocate)}
                  />
                  <DetailField label="Summary" value={profile.experienceSummary} wide />
                </div>
              </div>

              <div className="restaurant-detail-card">
                <div className="restaurant-detail-card-header">
                  <h3 className="restaurant-detail-section-title">Education</h3>
                </div>
                <div className="restaurant-detail-grid">
                  <DetailField label="Highest qualification" value={profile.highestQualification} />
                  <DetailField label="Course" value={profile.courseName} />
                  <DetailField label="Institution" value={profile.institutionName} />
                  <DetailField label="Passing year" value={profile.passingYear} />
                </div>
              </div>

              <div className="restaurant-detail-card">
                <div className="restaurant-detail-card-header">
                  <h3 className="restaurant-detail-section-title">Skills & compensation</h3>
                </div>
                <div className="restaurant-detail-grid">
                  <DetailField label="Skills" value={profile.skills ?? candidate.skills} wide />
                  <DetailField label="Languages" value={profile.languages} wide />
                  <DetailField
                    label="Expected salary"
                    value={formatSalary(profile.expectedSalary)}
                  />
                </div>
              </div>

              <div className="restaurant-detail-card">
                <div className="restaurant-detail-card-header">
                  <h3 className="restaurant-detail-section-title">Preferred locations</h3>
                </div>
                {preferredLocations.length > 0 ? (
                  <ul className="hiring-locations-modal-list hiring-candidate-detail-locations">
                    {preferredLocations.map((loc) => (
                      <li
                        key={getHiringLocationKey(loc)}
                        className="hiring-location-chip hiring-location-chip--modal"
                      >
                        <span className="hiring-location-chip-zip">
                          {formatHiringLocationPrimary(loc)}
                        </span>
                        <span className="hiring-location-chip-place">
                          {formatHiringLocationSecondary(loc)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="hiring-candidate-detail-empty">No preferred locations listed.</p>
                )}
              </div>

              <div className="restaurant-detail-card">
                <div className="restaurant-detail-card-header">
                  <h3 className="restaurant-detail-section-title">Record details</h3>
                </div>
                <div className="restaurant-detail-grid">
                  <DetailField label="Created" value={formatDetailDate(profile.createdAt)} />
                  <DetailField label="Last updated" value={formatDetailDate(profile.updatedAt)} />
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </RestaurantDashboardLayout>
  );
};

export default HiringCandidateDetail;
